import { UserPreferences, UserPreferencesUpdate } from '../types/user-preferences.types';
import { supabase } from '@/lib/supabase';
import { auth } from '@/core/auth/firebase';

interface SupabaseUserPreferences {
  id: string;
  user_id: string;
  display_name: string;
  subdomain: string;
  created_at: string;
  updated_at: string;
}

export class UserPreferencesRepository {
  private userId: string | null = null;

  constructor() {
    auth.onAuthStateChanged(user => {
      this.userId = user?.uid || null;
    });
  }

  private ensureAuthenticated(): string {
    const currentUser = auth.currentUser;

    if (!currentUser?.uid) {
      throw new Error('User must be authenticated to perform this operation');
    }

    if (!this.userId) {
      this.userId = currentUser.uid;
    }

    return this.userId;
  }

  async getUserPreferences(): Promise<UserPreferences | null> {
    const userId = this.ensureAuthenticated();

    try {
      const query = await supabase
        .from('user_preferences')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (query.error) {
        if (query.error.code === 'PGRST116') {
          // No rows found - user preferences don't exist yet
          return null;
        }
        throw query.error;
      }

      const preferences = query.data as SupabaseUserPreferences;

      return {
        id: preferences.id,
        userId: preferences.user_id,
        displayName: preferences.display_name,
        subdomain: preferences.subdomain,
        createdAt: new Date(preferences.created_at).getTime(),
        updatedAt: new Date(preferences.updated_at).getTime(),
      };
    } catch (error) {
      console.error('Error getting user preferences:', error);
      throw error;
    }
  }

  async updateUserPreferences(updates: UserPreferencesUpdate): Promise<UserPreferences> {
    const userId = this.ensureAuthenticated();

    try {
      // First, check if user preferences already exist
      const existingQuery = await supabase
        .from('user_preferences')
        .select('*')
        .eq('user_id', userId)
        .single();

      let query;
      if (existingQuery.data) {
        // Update existing record
        query = await supabase
          .from('user_preferences')
          .update({
            display_name: updates.displayName,
            subdomain: updates.subdomain,
            updated_at: new Date().toISOString(),
          })
          .eq('user_id', userId)
          .select()
          .single();
      } else {
        // Insert new record
        query = await supabase
          .from('user_preferences')
          .insert({
            user_id: userId,
            display_name: updates.displayName,
            subdomain: updates.subdomain,
            updated_at: new Date().toISOString(),
          })
          .select()
          .single();
      }

      if (query.error) throw query.error;

      const preferences = query.data as SupabaseUserPreferences;

      return {
        id: preferences.id,
        userId: preferences.user_id,
        displayName: preferences.display_name,
        subdomain: preferences.subdomain,
        createdAt: new Date(preferences.created_at).getTime(),
        updatedAt: new Date(preferences.updated_at).getTime(),
      };
    } catch (error) {
      console.error('Error updating user preferences:', error);
      throw error;
    }
  }

  async getSubdomainDisplayName(subdomain: string): Promise<string | null> {
    try {
      const query = await supabase
        .from('user_preferences')
        .select('display_name')
        .eq('subdomain', subdomain)
        .limit(1);

      if (query.error) throw query.error;

      return query.data.length > 0 ? (query.data[0] as SupabaseUserPreferences).display_name : null;
    } catch (error) {
      console.error('Error checking subdomain existence:', error);
      return null;
    }
  }

  async checkSubdomainExists(subdomain: string): Promise<boolean> {
    try {
      const query = await supabase
        .from('user_preferences')
        .select('id')
        .eq('subdomain', subdomain)
        .limit(1);

      if (query.error) throw query.error;

      return query.data.length > 0;
    } catch (error) {
      console.error('Error checking subdomain existence:', error);
      return false;
    }
  }

  async checkSubdomainAvailability(subdomain: string): Promise<boolean> {
    try {
      // Check reserved subdomains
      const reservedSubdomains = [
        'admin',
        'api',
        'www',
        'app',
        'mail',
        'support',
        'help',
        'blog',
        'docs',
      ];
      if (reservedSubdomains.includes(subdomain.toLowerCase())) {
        return false;
      }

      // Check if subdomain exists in database
      const exists = await this.checkSubdomainExists(subdomain);
      return !exists; // Available if it doesn't exist
    } catch (error) {
      console.error('Error checking subdomain availability:', error);
      return false;
    }
  }

  async deleteUserPreferences(): Promise<void> {
    const userId = this.ensureAuthenticated();

    try {
      const query = await supabase.from('user_preferences').delete().eq('user_id', userId);

      if (query.error) throw query.error;
    } catch (error) {
      console.error('Error deleting user preferences:', error);
      throw error;
    }
  }
}
